import sortBy from "utils/sortBy"

const isApplicable = (
  {
    page,
    layout,
    promotionalUnitId,
    articleId,
    hasPartners,
    classIds,
    productIds,
    category,
    subcategory,
    tags,
  },
  promotionalPlacement,
) => {
  const {
    targetPage,
    layoutPosition,
    targetArticleIDs,
    excludeArticleIDs,
    excludeCategories,
    excludeTags,
    includePartnerArticles,
    targetClasses,
    targetPlatforms,
    targetProducts,
    targetCategories,
    targetTags,
  } = promotionalPlacement
  if (page && page !== targetPage) {
    return false
  }

  if (targetPlatforms && !targetPlatforms.includes("Website")) {
    return false
  }

  if (layout && layout !== layoutPosition) {
    return false
  }

  if (articleId && excludeArticleIDs?.some((id) => Number(id) === Number(articleId))) {
    return false
  }

  if (category && excludeCategories?.some((c) => c.toLowerCase() === category.toLowerCase())) {
    return false
  }

  if (
    tags &&
    excludeTags?.some((tag) => tags.map((t) => t.toLowerCase()).includes(tag.toLowerCase()))
  ) {
    return false
  }

  if (!includePartnerArticles && hasPartners) {
    return false
  }

  if (promotionalUnitId === promotionalPlacement.contentful_id) {
    return true
  }

  if (articleId && targetArticleIDs?.some((id) => Number(id) === Number(articleId))) {
    return true
  }

  if (targetClasses && classIds?.some((id) => targetClasses.includes(id))) {
    return true
  }

  if (targetProducts && productIds?.some((id) => targetProducts.includes(id))) {
    return true
  }

  if (category && targetCategories?.some((c) => c.toLowerCase() === category.toLowerCase())) {
    return true
  }

  if (subcategory && targetCategories?.some((c) => c.toLowerCase() === subcategory.toLowerCase())) {
    return true
  }

  if (
    tags &&
    targetTags?.some((tag) => tags.map((t) => t.toLowerCase()).includes(tag.toLowerCase()))
  ) {
    return true
  }

  return false
}

const filter = (promotionalPlacements, props) =>
  // find promotional placements that are applicable
  promotionalPlacements.filter((promotionalPlacement) => isApplicable(props, promotionalPlacement))

const select = (
  article,
  articleProductIds,
  element,
  contentfulPromotionalPlacements,
  layout,
  index,
) => {
  let promotionalPlacements = []
  let selectedPlacement = null

  // target specific contentful promotional placement
  if ("promotionalUnitId" in element) {
    promotionalPlacements = filter(contentfulPromotionalPlacements, {
      page: "Article",
      layout,
      promotionalUnitId: element.promotionalUnitId,
    })
  }

  // if the specific contentful promotional placement is missing,
  // filter the placements by other criteria
  if (promotionalPlacements.length === 0) {
    const classIds = [
      ...("classId" in element ? [element.classId] : []),
      ...(article.featuredClass ? [article.featuredClass.id] : []),
    ]
    const productIds = [
      ...("productId" in element ? [element.productId] : []),
      ...articleProductIds,
    ]

    promotionalPlacements = filter(contentfulPromotionalPlacements, {
      page: "Article",
      layout,
      articleId: article.id,
      hasPartners: Boolean(article.partner),
      classIds,
      productIds,
      category: article.category,
      subcategory: article.subcategory,
      tags: [...(article.tags || []), ...(article.internalTags || [])],
    })
  }

  if (promotionalPlacements.length > 0) {
    // sort placements by visibility RPM based on their layout position
    if (layout === "In Content") {
      promotionalPlacements = sortBy(
        promotionalPlacements,
        index === 0 ? "highVisibilityRPM" : "mediumVisibilityRPM",
        "DESC",
      )
    } else if (layout === "Below Content") {
      promotionalPlacements = sortBy(promotionalPlacements, "lowVisibilityRPM", "DESC")
    }

    selectedPlacement = promotionalPlacements[0]
  }

  return selectedPlacement
}

const replacePromotionalPlacements = (article, contentfulPromotionalPlacements) => {
  // add contentful data to promotional placement body elements
  const filteredContentfulPromotionalPlacements = contentfulPromotionalPlacements.filter(
    ({ active, promotionStartDate, promotionEndDate }) =>
      active &&
      new Date(promotionStartDate) < new Date() &&
      (!promotionEndDate || new Date(promotionEndDate) > new Date()),
  )

  if (
    filteredContentfulPromotionalPlacements &&
    filteredContentfulPromotionalPlacements.length > 0
  ) {
    const productIds = article.body
      .filter(({ type, productId }) => type === "product" && productId)
      .map(({ productId }) => productId)

    let index = 0

    // promotional placements with "In Content" layout position
    article.body = article.body.map((element, idx) => {
      // don't replace the last element
      if (element.type === "promotional-placement" && idx < article.body.length - 2) {
        const placement = select(
          article,
          productIds,
          element,
          filteredContentfulPromotionalPlacements,
          "In Content",
          index,
        )
        if (placement) {
          index += 1
          return { ...element, ...placement, index }
        }
      }
      return element
    })

    // promotional placement with "Below Content" layout position
    const lastElement = article.body[article.body.length - 1]
    if (lastElement.type === "promotional-placement") {
      if (!article.featuredPromotionalPlacement) {
        const placement = select(
          article,
          productIds,
          lastElement,
          filteredContentfulPromotionalPlacements,
          "Below Content",
        )
        if (placement) {
          article.featuredPromotionalPlacement = {
            ...lastElement,
            ...placement,
            index,
            below: true,
          }
          index += 1
        }
      }
    }
  }
}

const getLayoutPromotionalPlacements = (article, contentfulPromotionalPlacements) => {
  const filteredContentfulPromotionalPlacements = contentfulPromotionalPlacements.filter(
    ({ active, promotionStartDate, promotionEndDate }) =>
      active &&
      new Date(promotionStartDate) < new Date() &&
      (!promotionEndDate || new Date(promotionEndDate) > new Date()),
  )

  const placements = {
    "In Content": { highVisibility: [], mediumVisibility: [] },
    "Below Content": { lowVisibility: [] },
  }

  if (filteredContentfulPromotionalPlacements?.length) {
    const inContentPlacements = filter(contentfulPromotionalPlacements, {
      page: "Article",
      layout: "In Content",
      articleId: article.id,
      hasPartners: Boolean(article.partner),
      category: article.category,
      subcategory: article.subcategory,
      tags: [...(article.tags || []), ...(article.internalTags || [])],
    })

    placements["In Content"].highVisibility = sortBy(
      inContentPlacements,
      "highVisibilityRPM",
      "DESC",
    )
    placements["In Content"].mediumVisibility = sortBy(
      inContentPlacements,
      "mediumVisibilityRPM",
      "DESC",
    )

    const belowContentPlacements = filter(contentfulPromotionalPlacements, {
      page: "Article",
      layout: "Below Content",
      articleId: article.id,
      hasPartners: Boolean(article.partner),
      category: article.category,
      subcategory: article.subcategory,
      tags: [...(article.tags || []), ...(article.internalTags || [])],
    })

    placements["Below Content"].lowVisibility = sortBy(
      belowContentPlacements,
      "lowVisibilityRPM",
      "DESC",
    )
  }

  return placements
}

export default { replacePromotionalPlacements, getLayoutPromotionalPlacements }
